import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';
import { Link, type LinkProps } from 'react-router-dom';
import { cx } from './cx.js';

/**
 * Every control that looks like a button, whether it acts or it navigates.
 *
 * Two kinds of `<button>` live in this app and only one of them is a control.
 * The other kind is an interactive *surface* — a card face, a grid cell, a row
 * in the review panel. Those have to be real buttons so a keyboard and a screen
 * reader can reach them (see `DESIGN-TABLE.md`), but they are pictures of
 * cards, and the button look would be nonsense on them.
 *
 * The look therefore lives in this file's variant table, and the bare element
 * carries a reset and nothing else. It used to be the other way round:
 * `button { ... }` styled all of them, and every surface undid it one property
 * at a time. That taught each new button that overriding the base was normal,
 * and the drift it produced was real — the hint toggle shipped with its own
 * radius, padding and type size, and looked like a different button from Leave
 * three feet away from it.
 *
 * So: **a real button gets a variant, never geometry of its own.** If a new one
 * needs a shape this does not have, the shape belongs in the variant table
 * below, beside the others, and in BRAND.md. `className` is for layout
 * and state (`grow`, a specimen's width floor), never for padding, radius,
 * height or type size. A toggle does not restyle itself either: `aria-pressed`
 * alone flips it to the ON state below.
 *
 * **Size is the second axis, and it is deliberately not a variant.** A variant
 * is what a button *means*; a size is how much room it takes. Folding `small`
 * into the variant list would have made a small primary unsayable, which is the
 * same conflation of look and geometry the paragraphs above spend their length
 * undoing. So there are two tables, `size` defaults to `md`, and a call site
 * that says nothing renders exactly the chip it rendered before. The geometry
 * that differs by size lives in `BUTTON_SIZE`; what every size shares stays in
 * `SHAPE`.
 *
 * `ButtonLink` is that same rule on a `<Link>`, and it exists because the rule
 * could not be obeyed without it. This file used to export `Button` alone,
 * which renders only `<button>`, so every control that navigated wrote the look
 * out by hand: seventeen of them across four screens. Thirteen wear this shape,
 * and four are the home menu's tiles, which have a shape and a component of
 * their own. They drifted the way copies always do. Every one of them dropped
 * the `not-disabled:` guards, and the loudest button in the Cross-Cribbage
 * tutorial lost its pressed state, so the one control the screen was built
 * around was the only one that did not move when a player pushed it.
 *
 * Both elements read one table. `buttonLook` is a variant standing still —
 * color, border, shadow, weight, and no state. `REACTION` is what the variant
 * does under the pointer, and both elements share it word for word, guards
 * included: `not-disabled:` compiles to `:not(:disabled)`, an `<a>` is never
 * `:disabled`, so on a link the guard simply always passes. Stripping it would
 * mean writing every hover out twice, because Tailwind reads source text and
 * cannot see a prefix removed at run time — and two copies of a hover is the
 * drift this component exists to end, moved one file closer to home. Only
 * `DISABLED` is button-only, because it is the one part that would be dead CSS
 * on a link.
 */

export type ButtonVariant =
  /** The one thing being asked for. Lemon, and there is one per view. */
  | 'primary'
  /** Quiet: navigation, dismissal, the things that are not the game. */
  | 'ghost'
  /** Rare fallback when primary, default and ghost do not fit. Card-like edges. */
  | 'subtle'
  /** Everything else — an ordinary control with no claim on the eye. */
  | 'default';

export type ButtonSize =
  /** The button this product ships. A 48px pill, and the default everywhere. */
  | 'md'
  /** The same chip drawn smaller, for a control that rides beside something else. */
  | 'sm'
  /** Icon only: a 44px circle, for a control at the end of a band or bar. */
  | 'icon'
  /** Icon only: a 28px circle, for a control that sits inside a field. */
  | 'icon-sm';

/**
 * A variant standing still: color, border, shadow, weight. No state in it, so a
 * component that wants the skin over a different shape can wear it.
 *
 * A button is a chip: a white or lemon face, a 2px ink stroke, and a hard
 * offset shadow of the same ink. Lemon is the one action color on a screen, so
 * `primary` is the only variant that carries it — if everything is lemon,
 * nothing is. `ghost` keeps the geometry and drops the chip: no face, no
 * stroke, no shadow, just ink text.
 *
 * Still exported, and `MenuTile` no longer reads it. A door sits one rung up
 * the shadow ladder from a chip, so the tile keeps its own two-line table
 * rather than wearing one that is wrong about its tier. Nothing outside this
 * file consumes this today; it stays exported because a second element wanting
 * a button's skin over a different shape is the case it was written for, and
 * that is one import away.
 *
 * The weight is here, once per variant, and not in the shape below with a
 * second one layered over it. That arrangement had been quietly losing: two
 * font-weight utilities on one element are decided by the order Tailwind emits
 * them, Tailwind emits that group in alphabetical order, and `font-medium` in
 * the shape therefore beat `font-extrabold` in the variant. Every primary button in
 * the product had been rendering at 500 rather than the weight this table asks
 * for, and the only place it looked right was the home menu, where four hand
 * copies had left `font-medium` out. One property, one class, and the cascade
 * has nothing to decide.
 *
 * All variants are `font-bold` now, because a chip is 700 in this brand and a
 * ghost standing beside one at 500 reads as a different family of control.
 */
export const buttonLook = {
  default: 'border-2 border-ink bg-ground-light font-bold text-ink shadow-sm shadow-ink',
  primary: 'border-2 border-ink bg-lemon font-bold text-ink shadow-sm shadow-ink',
  ghost: 'border-2 border-transparent bg-transparent font-bold text-ink shadow-none',
  subtle: 'border border-ink/25 bg-ground-light font-bold text-ink shadow-sm shadow-gray-400',
} satisfies Record<ButtonVariant, string>;

/**
 * What a variant does under the pointer. Both elements share it, guards and all.
 *
 * The press is a settle, never a bounce: the chip moves one pixel toward its own
 * shadow and the shadow shrinks by the same pixel, so the whole control looks
 * pushed down onto the page rather than thrown off it. A ghost has no shadow to
 * settle into, so it takes the nudge alone.
 *
 * The hover is spelled twice because a face and no face are two different
 * problems. `wash-ink` is the white chip's face already washed, so the chip
 * keeps a face while the pointer is on it; `bg-ink/5` is the same 5 percent
 * left translucent, which is what a ghost wants, because a ghost's whole
 * business is showing what it stands on. Both are background *colors*, and
 * that is the point: `transition-control` can interpolate a color, and the
 * background *image* this used to be it could not — every white chip snapped
 * instead of washing. `styles.css`'s `wash-ink` carries the measurement.
 */
const REACTION = {
  default:
    'hover:not-disabled:not-aria-pressed:not-aria-current:wash-ink active:not-disabled:shadow-xs',
  primary:
    'hover:not-disabled:not-aria-pressed:not-aria-current:bg-lemon-deep active:not-disabled:shadow-xs',
  ghost: 'hover:not-disabled:not-aria-pressed:not-aria-current:bg-ink/5',
  subtle:
    'hover:not-disabled:not-aria-pressed:not-aria-current:wash-ink active:not-disabled:shadow-xs',
} satisfies Record<ButtonVariant, string>;

/**
 * What a control does under the pointer, for the ones drawn outside `Button`.
 *
 * `buttonLook` was exported for the face; this is the reaction, and it exists
 * because four controls in `table/cross/` wrote the settle out by hand and had
 * already drifted three ways — two spellings of the hover wash, two of the
 * press, one control with no hover at all. A door that settles differently from
 * the chip above it reads as a different family, which is the drift the button
 * table exists to end.
 *
 * `press` is the settle `SHAPE` below applies to every button, quoted from the
 * same constant rather than restated. `sink` is the shadow half, for a control
 * that carries a chip shadow to shrink.
 *
 * The hover is two entries for the reason `REACTION` gives above: `wash` is for
 * a control with a face of its own — the match review's grid chips wear
 * `buttonLook.default` — and `washBare` for one drawn straight on the panel
 * behind it, like the review panel's rows. Handing a
 * faceless control the faced wash would paint it white on a cream ground; the
 * other way round leaves a faced one hollow.
 */
export const buttonReaction = {
  press: 'active:not-disabled:translate-x-px active:not-disabled:translate-y-px',
  wash: 'hover:not-disabled:wash-ink',
  washBare: 'hover:not-disabled:bg-ink/5',
  sink: 'active:not-disabled:shadow-xs',
} as const;

/**
 * The ON state, for the buttons that are toggles rather than acts.
 *
 * One treatment, and it is the recess: the button is pressed into the page —
 * `ground-dark`, the palette's recess — with the demi weight carrying the
 * squint. The text stays ink; nothing else could survive that face anyway.
 * A chip chosen from a row of siblings wears it (`ChipGroup`), and so does a
 * toggle standing alone: the lobby's Ready, the hint toggle, a stats filter.
 *
 * A lone toggle used to fill instead — gray-700 under white — on the argument
 * that ON with nothing beside it to compare against had to be loud. That is
 * retired. Two ON looks taught a player the same word twice, and the fill
 * that reads at switch dose went muddy at button dose: gray-700 across a 48px
 * face reads as faded ink beside true black strokes.
 *
 * The switch and the slider keep the fill, and their reason is about their
 * shapes rather than about how their state is read: the switch already wore
 * `ground-dark` in its second draft and vanished on a white panel (its header
 * tells the story), and the slider's empty groove *is* `ground-dark`, so a
 * recess fill would erase its own progress. Neither can recess, so neither
 * follows this line.
 *
 * It keys off `aria-pressed` itself, so the announced state and the drawn one
 * are a single attribute and cannot disagree: a toggle that forgets to say it
 * is pressed also fails to look it. And nothing chooses it per call site,
 * which is why it sits in `buttonClasses` beside the variant and the reaction
 * rather than arriving as a prop — a `<button>`, a `ButtonLink` and the bare
 * `<a>` cannot drift apart if none of them is asked. The prop that used to
 * pick a look is gone with the look it picked; `ButtonLink` hardcoded `fill`,
 * which is exactly the drift a second look makes available.
 *
 * Chosen beats hover. The `Select` menu settled that a wash dropped over the
 * answer already given is a downgrade, not a highlight — and here it is
 * load-bearing twice over, because the hover wash (`ink/5` over white
 * computes near `#F4F4F4`) sits close enough to `ground-dark` that a washed
 * chosen chip would smear the two states. The cascade will not say so on its
 * own — the hover wash's selector outranks this state's — so the wash carries
 * a `not-aria-pressed:` guard in the table above rather than betting on
 * emission order, the trap this file already documents twice.
 * `not-disabled:` keeps the disabled fade the winner on a control that is
 * somehow both off and on.
 *
 * `aria-current="true"` wears the same recess (2026-09-30). The current item
 * in a set of navigation buttons, the rules page's section rail, is the ON
 * state of a control that is not a toggle, and `MenuTile` already recesses
 * its current page the same way. It keys off the announced attribute for the
 * same reason `aria-pressed` does, and the hover wash stands down for it too.
 */
const PRESSED =
  'aria-pressed:not-disabled:bg-ground-dark aria-pressed:not-disabled:font-extrabold aria-current:not-disabled:bg-ground-dark aria-current:not-disabled:font-extrabold';

/**
 * The shape. Every variant is this shape; that is what makes them one family.
 *
 * The pill is `rounded-full`, and on a square box the same class draws a
 * circle, which is why the icon-only sizes need no shape of their own here.
 *
 * The border width used to be here, so a ghost was exactly the size of the chip
 * beside it: the border was always drawn and `ghost` only took its color away.
 * `subtle` broke that on 2026-09-10 by wanting a 1px card edge where the others
 * wear 2px, so the width moved into `buttonLook` and every variant now states
 * its own. The sizes still match — `box-sizing: border-box` means the outer
 * height is `min-h-12` either way — but the reason it holds is the box model
 * now, not one shared class, so a new variant owes its own border explicitly.
 *
 * What is left here is what does not change with size: the pill, the stroke,
 * the centering, the press. The padding, the gap, the type size and the height
 * floor moved into `BUTTON_SIZE` below when `sm` arrived — a property that two
 * sizes disagree about cannot also be stated once here, or the cascade decides
 * it, and this file has already paid for that twice.
 *
 * `min-w-12` moved into `BUTTON_SIZE` with the icon sizes. Both pill sizes
 * wear it, because the horizontal half of the 48px floor costs a small button
 * nothing worth saving. A circle cannot: a 48px floor on a 44px box would draw
 * an oval.
 */
const SHAPE = `inline-flex items-center justify-center rounded-full no-underline transition-control duration-150 ease-out ${buttonReaction.press}`;

/**
 * How much room a button takes. The second axis, and it holds no color.
 *
 * `md` is the button that has shipped since there was one: the 48px pill,
 * `px-5 py-2` and whatever type size it inherits. Its `gap-2`
 * keeps text and icons apart for buttons, links, and bare anchors alike;
 * callers must not add their own icon margins or gap overrides.
 *
 * `sm` is that chip drawn smaller — `px-4 py-2` (16 by 8px),
 * `text-sm` at 14px, `gap-2` between a mark and its word — for the places
 * a full chip is too loud to sit: a filter above a list, a control riding on a
 * panel header. It is a size and not a look, so a small primary and a small
 * ghost both exist and both wear the same lemon or the same nothing as their
 * full-size siblings.
 *
 * Padding uses standard Tailwind spacing: 1rem horizontally and 0.5rem
 * vertically. Type uses Tailwind’s `text-sm` at 0.875rem.
 *
 * **The pill shrinks and the target does not.** BRAND calls 48px
 * non-negotiable. `min-h-9` sets a 36px minimum; content determines the painted
 * height. A pseudo-element extends the target to 48px: `after:h-12`, centered
 * with `top-1/2` and a half-height translate, spanning the button with
 * `inset-x-0`. `PlayingCard` extends a card's target the same
 * way; this is that technique with the numbers named instead of bracketed.
 *
 * **No `after:pointer-events-none`, on purpose.** That utility is for a
 * pseudo-element drawn *over* a control that must not eat its clicks. Here
 * eating the click is the whole job: a pseudo-element is not an event target of
 * its own, so a press landing on it is attributed to the `<button>` that owns
 * it, and the added area is real thumb rather than decoration. Suppress the
 * pointer and the small button would be a sub-48px target wearing a comment
 * claiming otherwise. The cost is the one `PlayingCard` already pays: two `sm`
 * buttons stacked closer than `gap-3` have overlapping targets, so give a
 * column of them 12px or more.
 *
 * `relative` rides in this row rather than in `SHAPE` because `md` has no
 * pseudo-element to anchor and no business declaring a position. A shape that
 * said `relative` for everyone would be arguing with a caller's layout
 * `absolute` over emission order, and that trap is documented twice above.
 *
 * **`icon` and `icon-sm` are a circle around one mark and nothing else.** No
 * padding and no gap, because there is no word to keep apart from the mark;
 * the caller sizes the icon. The circle is the same pill on a square box, and
 * the target is the same pseudo-element pushed out on every side to 48px:
 * 2px past the 44px circle, 10px past the 28px one. Eighteen sketch sites, the
 * Cross band's two ends and the search field's clear drew this by hand, three
 * spellings of the target among them, before it was a size here (2026-09-24).
 * `icon` is 44 because the band's pill is 42 beside it. `icon-sm` is 28 because
 * a 3px focus ring at a 2px offset rings it out to 38, which clears the 44px
 * inside of a field (`TextInput` measured 32 touching the stroke). Wear these
 * with `ghost`; a faced circle has not been ruled on.
 */
const BUTTON_SIZE = {
  md: 'min-h-12 min-w-12 gap-2 px-5 py-2',
  sm: 'relative min-h-9 min-w-12 gap-2 px-4 py-2 text-sm after:absolute after:inset-x-0 after:top-1/2 after:h-12 after:-translate-y-1/2',
  icon: 'relative size-11 after:absolute after:-inset-0.5',
  'icon-sm': 'relative size-7 after:absolute after:-inset-2.5',
} satisfies Record<ButtonSize, string>;

/**
 * The one part a link has no use for: only a `<button>` can be disabled.
 *
 * A disabled chip keeps its outline and loses everything that claims the eye —
 * the lemon, the shadow, and most of the ink's contrast. It stays legible,
 * because a control you cannot read is a control you have lost. A ghost has no
 * chip to fade, so it fades only its text.
 *
 * `aria-disabled` wears the same face (BRAND, 2026-10-01), for a chip that
 * refuses presses for a moment and must keep the focus, which a `disabled`
 * button drops: the Hint chip while it thinks, the Seat peek's chips while an
 * action is out. The hover, the press and a faced chip's `aria-pressed`
 * recess above are guarded by `not-disabled`, which `aria-disabled` does not
 * trip, so each is overridden with the same guards plus `aria-disabled`, one
 * step more specific, rather than trusted to class order. The `aria-current`
 * recess, and a ghost's `aria-pressed` one, have no refused wearer yet and are
 * not overridden.
 */
const REFUSED_CHIP =
  'aria-disabled:cursor-default aria-disabled:border-gray-300 aria-disabled:bg-ground-light aria-disabled:text-gray-400 aria-disabled:shadow-none aria-disabled:hover:not-disabled:not-aria-pressed:not-aria-current:bg-ground-light aria-disabled:aria-pressed:not-disabled:bg-ground-light aria-disabled:aria-pressed:not-disabled:font-bold aria-disabled:active:not-disabled:translate-none aria-disabled:active:not-disabled:shadow-none';

const DISABLED_CHIP = `disabled:cursor-default disabled:border-gray-300 disabled:bg-ground-light disabled:text-gray-400 disabled:shadow-none ${REFUSED_CHIP}`;

const DISABLED = {
  default: DISABLED_CHIP,
  primary: DISABLED_CHIP,
  ghost:
    'disabled:cursor-default disabled:text-gray-400 aria-disabled:cursor-default aria-disabled:text-gray-400 aria-disabled:hover:not-disabled:not-aria-pressed:not-aria-current:bg-transparent aria-disabled:active:not-disabled:translate-none',
  subtle: DISABLED_CHIP,
} satisfies Record<ButtonVariant, string>;

/**
 * The class string for one control, so the variant table has exactly one reader.
 *
 * The variant, its reaction and the ON state are what every wearer gets; the
 * tail is what the elements do not share, in cascade order and ending in the
 * caller's `className`: a `<button>` passes its variant's `DISABLED`, a link
 * passes nothing.
 *
 * `size` sits second and defaults, so the one existing caller outside this file
 * — `dev/Gallery.tsx`, below — reads the same as it did. It is a typed union
 * rather than another loose string, which is what keeps the position safe: a
 * class string in that slot is a compile error, not a silently ignored size.
 *
 * **Exported for the third element, which is a plain `<a>`.** `ButtonLink`
 * wraps react-router's `Link`, so it takes `to` and routes inside the app; an
 * `href` that leaves the app — a raw asset, an external page — cannot use it
 * and was writing `<a><Button/></a>` instead. That is invalid HTML and two tab
 * stops for one control, the same defect ten screens were converted out of, and
 * it survived the sweep in `dev/Gallery.tsx` because the sweep read player
 * screens. A caller that needs a real `<a>` wears this and gets the table.
 */
export function buttonClasses(
  variant: ButtonVariant,
  size: ButtonSize = 'md',
  ...tail: readonly (string | undefined)[]
): string {
  return cx(SHAPE, BUTTON_SIZE[size], buttonLook[variant], REACTION[variant], PRESSED, ...tail);
}

/**
 * `style` is omitted from every primitive's passthrough, here and in the nine
 * files beside it. It is the one channel that carries arbitrary CSS past both
 * brand hooks — `brand-markup-check.sh` and the bracket ratchet read
 * `className` and nothing else — so a caller could freeze a copy of a token in
 * an inline declaration and no check would see it. Nothing passed one; closing
 * it costs nobody anything. `Slider` is the exception, and it is not a
 * passthrough: it writes `--slider-fill` itself.
 */
export interface ButtonProps extends Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'className' | 'style' | 'type'
> {
  readonly variant?: ButtonVariant;
  /** How much room it takes. `md` unless the screen has a reason. */
  readonly size?: ButtonSize;
  /**
   * `button` unless a form genuinely needs submitting. Defaulted rather than
   * required because the HTML default is `submit`, and a control inside a form
   * that forgets to say so submits it — a bug that only appears on the one
   * screen with a form on it.
   */
  readonly type?: 'button' | 'submit';
  /** Layout and state only. Never geometry — see the note above. */
  readonly className?: string | undefined;
  /**
   * The element itself, for the callers that have to measure or focus it — the
   * settings sheet anchors its popover on the gear. A plain prop rather than
   * `forwardRef`, which React 19 no longer needs.
   */
  readonly ref?: Ref<HTMLButtonElement> | undefined;
  readonly children: ReactNode;
}

export function Button({
  variant = 'default',
  size = 'md',
  type = 'button',
  className,
  children,
  ...rest
}: ButtonProps): React.JSX.Element {
  return (
    <button
      type={type}
      className={buttonClasses(variant, size, DISABLED[variant], className)}
      {...rest}
    >
      {children}
    </button>
  );
}

export interface ButtonLinkProps extends Omit<LinkProps, 'className' | 'style'> {
  readonly variant?: ButtonVariant;
  /** How much room it takes. `md` unless the screen has a reason. */
  readonly size?: ButtonSize;
  /** Layout and state only. Never geometry — see the note above. */
  readonly className?: string | undefined;
  readonly children: ReactNode;
}

/**
 * A button that navigates. Same look, same table, a real `<a>` underneath.
 *
 * Everything `Link` takes passes straight through — `to`, `replace`, `state`,
 * `target`, `aria-*`, `onClick` — because the reason a screen reaches for this
 * instead of a `Button` with a `navigate()` call is that it wants a link: one a
 * player can middle-click, copy, or open in a new tab.
 */
export function ButtonLink({
  variant = 'default',
  size = 'md',
  className,
  children,
  ...rest
}: ButtonLinkProps): React.JSX.Element {
  return (
    <Link className={buttonClasses(variant, size, className)} {...rest}>
      {children}
    </Link>
  );
}
